import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { Clock, Check, ArrowLeft, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AnimatedSection({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className={className}>
      {children}
    </motion.div>
  );
}

const catLabels = { course: "دورة", workshop: "ورشة عمل", program: "برنامج" };

export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [regCourse, setRegCourse] = useState(null);
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [regCounts, setRegCounts] = useState({});

  useEffect(() => {
    axios.get(`${API}/courses`).then(r => {
      setCourses(r.data);
      r.data.forEach(c => {
        axios.get(`${API}/courses/${c.id}/registrations/count`).then(res => {
          setRegCounts(prev => ({ ...prev, [c.id]: res.data.count }));
        }).catch(() => {});
      });
    }).catch(() => {});
  }, []);

  const handleRegister = async () => {
    if (!regForm.name || !regForm.phone) {
      toast.error("الاسم ورقم الجوال مطلوبين");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/courses/${regCourse.id}/register`, regForm);
      toast.success("تم التسجيل بنجاح! سنتواصل معك قريباً");
      setRegCourse(null);
      setRegForm({ name: "", email: "", phone: "", notes: "" });
      setRegCounts(prev => ({ ...prev, [regCourse.id]: (prev[regCourse.id] || 0) + 1 }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "حدث خطأ");
    }
    setSubmitting(false);
  };

  const filtered = filter === "all" ? courses : courses.filter(c => c.category === filter);

  return (
    <div data-testid="courses-page">
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#111112]">
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-primary/8 rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 text-primary text-sm font-medium mb-4">الدورات والبرامج</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Zain'] text-white leading-tight mb-4">
              طوّر <span className="text-primary">مهاراتك</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl">
              مجموعة من الدورات والبرامج التدريبية المصممة لتطوير مهاراتك المهنية.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="courses-filters">
            {[{ key: "all", label: "الكل" }, { key: "course", label: "دورات" }, { key: "workshop", label: "ورش عمل" }, { key: "program", label: "برامج" }].map(c => (
              <Button
                key={c.key}
                variant={filter === c.key ? "default" : "outline"}
                className={filter === c.key ? "bg-primary hover:bg-[#E54D2E] text-white" : "border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"}
                onClick={() => setFilter(c.key)}
                data-testid={`course-filter-${c.key}`}
              >
                {c.label}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((course, i) => (
              <AnimatedSection key={course.id}>
                <div className="bg-[#161618] border border-white/5 rounded-xl overflow-hidden hover:border-primary/20 transition-all group" data-testid={`course-card-${i}`}>
                  <div className="aspect-video overflow-hidden relative">
                    <img src={course.image_url} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <Badge className="absolute top-3 right-3 bg-primary/90 text-white">
                      {catLabels[course.category] || course.category}
                    </Badge>
                    {regCounts[course.id] > 0 && (
                      <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur-sm text-zinc-300 text-xs">
                        <Users size={12} />
                        {regCounts[course.id]} مسجّل
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold font-['Zain'] text-white mb-2">{course.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4">{course.description}</p>
                    <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
                      <span className="flex items-center gap-1.5">
                        <Clock size={14} className="text-primary" />
                        {course.duration}
                      </span>
                      <span className="text-primary font-bold text-base">{course.price}</span>
                    </div>
                    {course.features?.length > 0 && (
                      <ul className="space-y-1.5 mb-5">
                        {course.features.map((f, fi) => (
                          <li key={fi} className="flex items-center gap-2 text-zinc-400 text-sm">
                            <Check size={14} className="text-primary flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}
                    {course.registration_open !== false ? (
                      <Button
                        className="w-full bg-primary hover:bg-[#E54D2E] text-white"
                        onClick={() => setRegCourse(course)}
                        data-testid={`course-register-${i}`}
                      >
                        سجّل الآن
                        <ArrowLeft size={14} className="ms-2" />
                      </Button>
                    ) : (
                      <Button className="w-full bg-zinc-700 text-zinc-400 cursor-not-allowed" disabled data-testid={`course-closed-${i}`}>
                        التسجيل مغلق
                      </Button>
                    )}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-zinc-500">
              لا توجد دورات في هذا التصنيف حاليًا
            </div>
          )}
        </div>
      </section>

      {/* Registration Dialog */}
      <Dialog open={!!regCourse} onOpenChange={(open) => !open && setRegCourse(null)}>
        <DialogContent className="bg-[#161618] border-white/10 text-white max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-white font-['Zain'] text-xl">التسجيل في الدورة</DialogTitle>
          </DialogHeader>
          {regCourse && (
            <div className="space-y-4 mt-2">
              <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                <p className="text-primary font-bold text-sm">{regCourse.title}</p>
                <p className="text-zinc-500 text-xs mt-1">{regCourse.duration} • {regCourse.price}</p>
              </div>
              <div className="space-y-3">
                <Input
                  value={regForm.name}
                  onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                  placeholder="الاسم الكامل *"
                  className="bg-background border-white/10 text-white text-right"
                  data-testid="reg-name"
                />
                <Input
                  value={regForm.phone}
                  onChange={e => setRegForm({ ...regForm, phone: e.target.value })}
                  placeholder="رقم الجوال *"
                  className="bg-background border-white/10 text-white"
                  dir="ltr"
                  data-testid="reg-phone"
                />
                <Input
                  value={regForm.email}
                  onChange={e => setRegForm({ ...regForm, email: e.target.value })}
                  placeholder="البريد الإلكتروني (اختياري)"
                  className="bg-background border-white/10 text-white"
                  dir="ltr"
                  data-testid="reg-email"
                />
                <Input
                  value={regForm.notes}
                  onChange={e => setRegForm({ ...regForm, notes: e.target.value })}
                  placeholder="ملاحظات (اختياري)"
                  className="bg-background border-white/10 text-white text-right"
                  data-testid="reg-notes"
                />
              </div>
              <Button
                className="w-full bg-primary hover:bg-[#E54D2E] text-white"
                onClick={handleRegister}
                disabled={submitting}
                data-testid="reg-submit"
              >
                {submitting ? "جارٍ التسجيل..." : "تأكيد التسجيل"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
